import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canManage, can, isCleaner } from "@/lib/perms";
import { firstName } from "@/lib/user";
import { StatusBadge, SeverityBadge } from "@/components/Badges";
import { CONDITION_LABELS } from "@/lib/constants";
import { todayIso, longDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { BriefingText } from "@/components/BriefingText";
import PayoutSetup from "@/components/PayoutSetup";
import { DirectionsButton } from "@/components/DirectionsButton";
import { PortfolioWeather } from "@/components/PortfolioWeather";
import { StormPlaybooks } from "@/components/StormPlaybooks";
import { toast } from "sonner";
import {
  Home, CalendarClock, AlertTriangle, CheckCircle2, ArrowRight, UserPlus, Flame, Clock, Plus,
  Sparkles, Loader2,
} from "lucide-react";

const Stat = ({ icon: Icon, value, text, testId, tone = "royal", to }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`block rounded-lg border bg-white p-5 hover:shadow-md hover:-translate-y-[2px] transition-transform duration-200 ${
      tone === "alert" && value > 0 ? "border-red-200" : "border-border"
    }`}
  >
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{text}</p>
      <Icon className={`h-4 w-4 ${tone === "alert" && value > 0 ? "text-red-500" : tone === "gold" ? "text-gold" : "text-royal"}`} />
    </div>
    <p className="font-display text-3xl font-bold text-royal-deep mt-3">{value}</p>
    <p className="mt-2 text-[11px] font-medium text-royal/70">View →</p>
  </Link>
);

const JobRow = ({ job, testId }) => (
  <Link
    to={`/jobs/${job._id}`}
    data-testid={testId}
    className="flex items-center gap-4 px-5 py-3.5 hover:bg-secondary/60 transition-colors duration-200"
  >
    <div className="w-14 text-center shrink-0">
      <p className="text-[10px] uppercase text-muted-foreground">
        {new Date(job.scheduled_date + "T12:00:00").toLocaleDateString(undefined, { month: "short" })}
      </p>
      <p className="font-display text-lg font-bold text-royal-deep leading-none">
        {new Date(job.scheduled_date + "T12:00:00").getDate()}
      </p>
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-royal-deep truncate">{job.property_name}</p>
      <p className="text-xs text-muted-foreground truncate">
        {job.work_type_label} · {job.scheduled_time} · {(job.crew_names || []).join(", ") || "No one assigned"}
      </p>
    </div>
    <StatusBadge value={job.status} />
  </Link>
);

const Panel = ({ title, icon: Icon, tone, count, children, testId, action }) => (
  <section data-testid={testId} className="rounded-lg border border-border bg-white overflow-hidden">
    <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
      <Icon className={`h-4 w-4 ${tone === "alert" ? "text-red-500" : tone === "gold" ? "text-gold" : "text-royal"}`} />
      <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">{title}</h2>
      {count > 0 && (
        <span className="rounded-full bg-royal/5 border border-royal/10 px-2 py-0.5 text-[11px] font-semibold text-royal">
          {count}
        </span>
      )}
      <div className="ml-auto">{action}</div>
    </div>
    {children}
  </section>
);

export default function Dashboard() {
  const { user } = useAuth();
  const manager = canManage(user);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [issues, setIssues] = useState([]);
  const [properties, setProperties] = useState([]);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/insights/stats").then(({ data }) => setStats(data));
    api.get("/jobs", { params: { page_size: 200 } }).then(({ data }) => setJobs(data));
    api.get("/issues", { params: { status: "open" } }).then(({ data }) => setIssues(data));
    api.get("/properties").then(({ data }) => setProperties(data));
    if (manager) api.get("/insights/portfolio/summary").then(({ data }) => setBrief(data));
  }, [manager]);

  useEffect(() => {
    load();
  }, [load]);

  const generateBrief = async () => {
    setBriefBusy(true);
    try {
      const { data } = await api.post("/insights/portfolio/summary");
      setBrief(data);
      toast.success("Portfolio briefing generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate briefing");
    }
    setBriefBusy(false);
  };

  const today = todayIso();
  const todayJobs = jobs.filter((j) => j.scheduled_date === today);
  const unassigned = jobs.filter((j) => j.status === "pending");
  const upcoming = jobs.filter((j) => j.scheduled_date > today && j.status !== "completed").slice(0, 5);
  const urgent = issues.filter((i) => i.severity === "urgent" || i.severity === "high");
  const otherIssues = issues.filter((i) => !urgent.includes(i));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">
            {manager ? "Operations command" : "Your assignments"}
          </p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2 break-words">
            Good day, {firstName(user)}.
          </h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            {longDate()}
          </p>
        </div>
        {manager && (
          <div className="flex gap-2">
            <Link
              to="/schedule"
              data-testid="quick-schedule-link"
              className="inline-flex items-center gap-2 rounded-md bg-royal px-4 py-2.5 text-sm font-semibold text-white hover:bg-royal-deep hover:-translate-y-[1px] transition-transform duration-200"
            >
              <Plus className="h-4 w-4" /> New work order
            </Link>
            <Link
              to="/properties"
              data-testid="quick-property-link"
              className="inline-flex items-center gap-2 rounded-md border border-gold bg-gold/10 px-4 py-2.5 text-sm font-semibold text-royal-deep hover:bg-gold/20 transition-colors duration-200"
            >
              <Home className="h-4 w-4 text-gold" /> Properties
            </Link>
          </div>
        )}
      </div>

      {isCleaner(user) && <PayoutSetup />}
      <PortfolioWeather />
      <StormPlaybooks />
      {stats && (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-5" data-testid="stats-grid">
          <Stat icon={Home} value={stats.properties} text="Properties" testId="stat-properties" to="/properties" />
          <Stat icon={CalendarClock} value={stats.jobs_today} text="Jobs today" testId="stat-jobs-today" tone="gold" to="/schedule?date=today" />
          <Stat icon={UserPlus} value={stats.jobs_unassigned} text="Unassigned" testId="stat-unassigned" tone="alert" to="/schedule?status=pending" />
          <Stat icon={Flame} value={stats.urgent_issues} text="Urgent issues" testId="stat-urgent-issues" tone="alert" to="/issues?severity=urgent" />
          <Stat icon={CheckCircle2} value={stats.jobs_completed} text="Completed" testId="stat-jobs-completed" to="/schedule?status=completed" />
        </div>
      )}

      {manager && (
        <div className="ai-card p-5 sm:p-6" data-testid="portfolio-ai-card">
          <div className="flex flex-wrap items-center gap-3">
            <Sparkles className="h-4 w-4 text-gold" />
            <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">
              Portfolio briefing
            </h2>
            <Button
              data-testid="generate-portfolio-brief-btn"
              onClick={generateBrief}
              disabled={briefBusy}
              className="ml-auto bg-gold hover:bg-gold/90 text-royal-deep font-semibold h-10"
            >
              {briefBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : brief ? "Regenerate" : "Generate briefing"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            AI-written overview across every property — condition, risks and what to do next.
          </p>
          {brief ? (
            <>
              <BriefingText text={brief.summary} testId="portfolio-ai-text" />
              <p className="mt-3 text-[11px] text-muted-foreground">
                Generated {new Date(brief.generated_at).toLocaleString()}
              </p>
            </>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">No briefing generated yet.</p>
          )}
        </div>
      )}

      <Panel
        title="Today's work"
        icon={Clock}
        tone="gold"
        count={todayJobs.length}
        testId="today-jobs-card"
        action={
          <Link to="/schedule" data-testid="view-schedule-link" className="text-xs font-semibold text-royal hover:text-gold transition-colors duration-200 flex items-center gap-1">
            Full schedule <ArrowRight className="h-3 w-3" />
          </Link>
        }
      >
        <div className="divide-y divide-border">
          {todayJobs.length === 0 && (
            <p className="p-5 text-sm text-muted-foreground">Nothing scheduled for today.</p>
          )}
          {todayJobs.map((j) => (
            <JobRow key={j._id} job={j} testId={`today-job-${j._id}`} />
          ))}
        </div>
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        {can(user, "job:assign") && (
        <Panel
          title="Needs a cleaner"
          icon={UserPlus}
          tone="alert"
          count={unassigned.length}
          testId="unassigned-card"
          action={
            <Link to="/schedule?status=pending" data-testid="view-unassigned-link" className="text-xs font-semibold text-royal hover:text-gold transition-colors duration-200 flex items-center gap-1">
              Assign now <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          <div className="divide-y divide-border">
            {unassigned.length === 0 && <p className="p-5 text-sm text-muted-foreground">Every work order has someone assigned.</p>}
            {unassigned.slice(0, 5).map((j) => (
              <JobRow key={j._id} job={j} testId={`unassigned-job-${j._id}`} />
            ))}
          </div>
        </Panel>
        )}

        <Panel
          title="Urgent & high issues"
          icon={AlertTriangle}
          tone="alert"
          count={urgent.length}
          testId="urgent-issues-card"
          action={
            <Link to="/issues" data-testid="view-issues-link" className="text-xs font-semibold text-royal hover:text-gold transition-colors duration-200 flex items-center gap-1">
              All issues <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          <div className="divide-y divide-border">
            {urgent.length === 0 && otherIssues.length === 0 && (
              <p className="p-5 text-sm text-muted-foreground">No open issues. Everything looks great.</p>
            )}
            {[...urgent, ...otherIssues].slice(0, 5).map((i) => (
              <Link
                key={i._id}
                to={`/issues?focus=${i._id}`}
                data-testid={`dashboard-issue-${i._id}`}
                className="block px-5 py-3.5 hover:bg-secondary/60 transition-colors duration-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-royal-deep">{i.title}</p>
                  <SeverityBadge value={i.severity} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{i.property_name}</p>
              </Link>
            ))}
          </div>
        </Panel>
      </div>

      {upcoming.length > 0 && (
        <Panel
          title="Coming up next"
          icon={CalendarClock}
          count={upcoming.length}
          testId="upcoming-jobs-card"
          action={
            <Link to="/schedule?view=month" data-testid="view-calendar-link" className="text-xs font-semibold text-royal hover:text-gold transition-colors duration-200 flex items-center gap-1">
              Calendar <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          <div className="divide-y divide-border">
            {upcoming.map((j) => (
              <JobRow key={j._id} job={j} testId={`upcoming-job-${j._id}`} />
            ))}
          </div>
        </Panel>
      )}

      <section data-testid="properties-strip">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Properties at a glance</h2>
          <Link to="/properties" data-testid="view-properties-link" className="text-xs font-semibold text-royal hover:text-gold transition-colors duration-200">
            View all
          </Link>
        </div>
        {properties.length === 0 ? (
          <div className="rounded-lg border border-dashed border-royal/25 bg-white p-10 text-center">
            <Home className="h-7 w-7 text-gold mx-auto" />
            <p className="font-display text-lg font-semibold text-royal-deep mt-3">No properties yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              {can(user, "property:create")
                ? "Add your first property to start scheduling cleans."
                : "Properties you own will appear here once your management company adds them."}
            </p>
            {manager && (
              <Link
                to="/properties"
                data-testid="empty-add-property-link"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-royal px-4 py-2.5 text-sm font-semibold text-white hover:bg-royal-deep transition-colors duration-200"
              >
                <Plus className="h-4 w-4" /> Add a property
              </Link>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {properties.slice(0, 3).map((p, idx) => (
              <Link
                key={p._id}
                to={`/properties/${p._id}`}
                data-testid={`dashboard-property-${p._id}`}
                style={{ animationDelay: `${idx * 70}ms` }}
                className="rise rounded-lg border border-border bg-white overflow-hidden hover:shadow-md hover:-translate-y-[2px] transition-transform duration-200"
              >
                <div className="h-36 bg-secondary overflow-hidden relative">
                  {p.photos?.[0] ? (
                    <img src={fileUrl(p.photos[0])} alt={p.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">No photo</div>
                  )}
                  <div className="absolute top-2.5 right-2.5 z-10">
                    <DirectionsButton property={p} size="sm" />
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-display font-semibold text-royal-deep">{p.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{p.address}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-[11px] rounded-full bg-royal/5 border border-royal/10 px-2 py-0.5 text-royal">
                      {CONDITION_LABELS[p.condition] || p.condition}
                    </span>
                    {p.open_issues > 0 && (
                      <span className="text-[11px] rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-red-700">
                        {p.open_issues} open
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
